<template>
    <div class="email-form-input-wrap">
        <div class="input-container">
            <div class="list-element" v-for="(email, index) in emailList" :key="index" :class="[classEmailBox(email), { 'is-selected': selectedEmail && selectedEmail === email }]">
                <span class="list-element-email">{{ email }}</span>
                <button class="remove-element-button" type="button" @click="removeListElement(email)" :class="classEmailBox(email)">
                    <font-awesome-icon icon="fa-solid fa-xmark" />
                </button>
            </div>
            <input 
                id="emailforminput-box"
                @input="onTextInput"
                @keydown.enter="onTextInput"
                @keydown.backspace="onBackspace"
                @keydown.down.prevent="onArrowPress()"
                @keydown.up.prevent="onArrowPress()"
                class="input ghost-input" 
                type="text" 
                v-model="inputText" 
                :placeholder="emailPlaceholder" 
                :required="emailList.length === 0"
                autocomplete="off"
            />
        </div>
        <div class="relative-container">
            <DropdownContainer
                :showContainer="showEmailSuggestionList"
                @closeDropdown="onDropdownClose">

                <EmailSuggestionList 
                    ref="email-suggestion-list"
                    :searchText="inputText"
                    focusable
                    @emailSelected="onEmailSelected" />
            </DropdownContainer>
        </div>
    </div>
</template>

<script>
import DropdownContainer from "@/components/ui/DropdownContainer";
import EmailSuggestionList from "@/components/documents/EmailSuggestionList";

export default {
    name: "EmailFromInput",
    components: {EmailSuggestionList, DropdownContainer},
    props: {
        preselected: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            inputText: "",
            emailList: [],
            showEmailSuggestionList: false,
            selectedEmail: null
        }
    },
    computed: {
        emailPlaceholder() {
            if (!this.emailList.length)
                return this.$gettext('jane@example.com');
            return "";
        }
    },
    created () {
        // Preset emails based on preselected prop
        if (this.preselected) {
            let preselected = this.preselected.replace(/\s/g, '').split(',')
            preselected = preselected.filter(email => email !== '')
            this.emailList = preselected
        }
    },
    methods: {
        onTextInput(ev) {
            // Typing clears any selection
            if (this.selectedEmail) this.selectedEmail = null

            if (this.inputText.length > 0) {
                this.showEmailSuggestionList = true;
            } else {
                this.showEmailSuggestionList = false;
            }

            if (ev.data === ',' || ev.data === ' ' || ev.key === 'Enter') {
                this._processEmailInput();
                this.showEmailSuggestionList = false;
            }
        },
        removeListElement(element) {
            this.emailList = this.emailList.filter((el) => el != element);
        },
        classEmailBox(email) {
            if (!this.validateEmailAddress(email))
                return "danger-box";
        },
        validateEmailAddress(emailAddress) {
            var validRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailAddress.match(validRegex);
        },
        getFullString() {
            let fullString = "";

            this._processEmailInput();

            this.emailList.forEach((email) => {
                if (fullString.length)
                    fullString = fullString + ", " + email;
                else
                    fullString = email;
            });

            if (this.inputText.length)
                fullString = fullString + ", " + this.inputText;

            return fullString;
        },
        _processEmailInput() {
            let text = this.inputText.replace(/,/g, " ");
            let stringArray = text.split(" ");
            stringArray = stringArray.filter((string) => string.length);
            
            stringArray.forEach((element) => {
                if (!this.emailList.includes(element))
                    this.emailList.push(element);
            });

            stringArray.forEach((element) => {
                text = text.replace(element, "");
            });
            text = text.replace(" ", "");

            this.inputText = text;
        },
        onDropdownClose() {
            this.showEmailSuggestionList = false;
        },
        onEmailSelected(customer) {
            this.onDropdownClose();
            this.inputText = customer.email;
            this.selectedEmail = null // ensure no selection lingers
            this._processEmailInput();
            document.getElementById("emailforminput-box").focus();
        },
        onArrowPress() {
            if (this.$refs['email-suggestion-list']) {
                this.$refs['email-suggestion-list'].focusOnList(0)
            }
        },
        onBackspace(e) {
            if (this.inputText.length > 0) return
            if (this.emailList.length === 0) return

            const last = this.emailList[this.emailList.length - 1]

            if (!this.selectedEmail) {
                // First backspace: select the last chip
                this.selectedEmail = last
                return
            }
            // Second backspace (while selected): remove it
            // (Also handles if selection somehow isn't the last: we remove the actual last)
            if (this.selectedEmail === last) {
                this.emailList = this.emailList.slice(0, -1)
                this.selectedEmail = null
            } else {
                // Re-sync selection to last if out of sync
                this.selectedEmail = last
            }
        },
    }
}
</script>

<style lang="scss">
    .email-form-input-wrap {
        width: 100%;
    }
</style>
<style lang="scss" scoped>
.input-container {
    display: flex;
    flex-direction: row;
    position: relative;
    overflow: auto;
    max-height: 13rem;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
    border-radius: 6px;
    padding: 0.375rem 0.75rem;
    gap: 0.5rem;
}

@media(max-width: 840px) {
    .input-container {
        max-height: 6rem;
    }
}

.input {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 300px;
    border: none;
    &.ghost-input {
        background: transparent;
        &:focus {
            outline: none;
        }
    }
}

.list-element {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    background: white;
    border-radius: $radiusXl;
    font-size: 14px;
    box-shadow: $emailInputItemShadow;
    padding: 0;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
    &.is-selected {
        background: #c2e7ff;
        box-shadow: $emailInputItemSelectedShadow;
    }
    .remove-element-button {
        display: flex;
        border: none;
        background: none;
        font-size: 15px;
        opacity: 0.6;
        padding: 0;
    }
}



.relative-container {
    position: relative;
}

.danger-box {
    background-color: $colorDangerBg;
}
</style>